/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Research Tab - Modular component for R&D management
 */

import React, { useMemo, useState } from 'react';
import { CityStats, ResearchState } from '../../../types';
import { TECH_TREE } from '../../../constants';
import { playUiSfx } from '../../../utils/uiAudio';

interface ResearchTabProps {
  stats: CityStats;
  research: ResearchState;
  onStartResearch: (id: string) => void;
}

const ResearchTab: React.FC<ResearchTabProps> = ({ stats, research, onStartResearch }) => {
  const [filterTier, setFilterTier] = useState<'all' | 'tier1' | 'tier2' | 'tier3'>('all');
  const [showCompleted, setShowCompleted] = useState(true);

  // Calculate research metrics
  const researchMetrics = useMemo(() => {
    const totalProjects = TECH_TREE.length;
    const completedProjects = research.completedTechs.length;
    const completionPercentage = Math.round((completedProjects / totalProjects) * 100);
    
    const tier1 = research.completedTechs.filter(t => t.includes('tech_basic')).length;
    const tier2 = research.completedTechs.filter(t => 
      t.includes('tech_compliance') || t.includes('tech_ops') || t.includes('tech_adv_cyber')
    ).length;
    const tier3 = research.completedTechs.filter(t => 
      t.includes('tech_zerotrust') || t.includes('tech_admin') || t.includes('tech_soc2') || 
      t.includes('tech_cloud') || t.includes('tech_threat') || t.includes('tech_gdpr') || 
      t.includes('tech_advanced') || t.includes('tech_regulatory')
    ).length;

    return { totalProjects, completedProjects, completionPercentage, tier1, tier2, tier3 };
  }, [research.completedTechs]);

  // Filter tech tree
  const filteredTech = useMemo(() => {
    let techs = TECH_TREE;
    
    if (!showCompleted) {
      techs = techs.filter(t => !research.completedTechs.includes(t.id));
    }
    
    if (filterTier !== 'all') {
      if (filterTier === 'tier1') {
        techs = techs.filter(t => t.prerequisites.length === 0);
      } else if (filterTier === 'tier2') {
        techs = techs.filter(t => t.prerequisites.length === 1);
      } else if (filterTier === 'tier3') {
        techs = techs.filter(t => t.prerequisites.length >= 2);
      }
    }
    
    return techs;
  }, [filterTier, showCompleted, research.completedTechs]);

  const activeProject = research.activeResearchId 
    ? TECH_TREE.find(t => t.id === research.activeResearchId) 
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-white/10 pb-4">
        <h3 className="text-xl font-bold text-white uppercase tracking-wider">Research & Development</h3>
        <p className="text-slate-400 mt-2">Unlock new buildings and capabilities through research</p>
      </div>

      {/* Research Progress Overview */}
      <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border border-cyan-700/30 rounded-lg p-5">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          <div>
            <h4 className="text-lg font-bold text-white flex items-center gap-2">
              <span>🔬</span> Master R&D Progress
            </h4>
            <p className="text-sm text-slate-400">
              {researchMetrics.completedProjects} of {researchMetrics.totalProjects} projects completed
            </p>
          </div>
          <div className="text-3xl font-black text-cyan-400">
            {researchMetrics.completionPercentage}%
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-slate-800 rounded-full h-4 mb-4 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-cyan-600 to-emerald-500 transition-all duration-500 relative"
            style={{ width: `${researchMetrics.completionPercentage}%` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
          </div>
        </div>

        {/* Tier Progress */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-black/30 rounded-lg">
            <div className="text-xs text-slate-400 uppercase mb-1">Tier 1</div>
            <div className="text-xl font-bold text-white">{researchMetrics.tier1}/1</div>
            <div className="text-xs text-cyan-400">Foundation</div>
          </div>
          <div className="text-center p-3 bg-black/30 rounded-lg">
            <div className="text-xs text-slate-400 uppercase mb-1">Tier 2</div>
            <div className="text-xl font-bold text-white">{researchMetrics.tier2}/3</div>
            <div className="text-xs text-purple-400">Advanced</div>
          </div>
          <div className="text-center p-3 bg-black/30 rounded-lg">
            <div className="text-xs text-slate-400 uppercase mb-1">Tier 3</div>
            <div className="text-xl font-bold text-white">{researchMetrics.tier3}/7</div>
            <div className="text-xs text-emerald-400">Expert</div>
          </div>
        </div>
      </div>

      {/* Active Research */}
      {activeProject && (
        <div className="bg-cyan-900/20 border border-cyan-500/50 rounded-lg p-5 shadow-[0_0_20px_rgba(6,182,212,0.2)]">
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className="text-xs text-cyan-400 uppercase font-bold mb-1">🔄 Currently Researching</div>
              <h4 className="text-lg font-bold text-white">{activeProject.name}</h4>
              <p className="text-sm text-slate-400">{activeProject.description}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-black text-cyan-400">
                {Math.floor(research.researchProgress * 100)}%
              </div>
              <div className="text-xs text-slate-400">Complete</div>
            </div>
          </div>
          <div className="w-full bg-slate-800 rounded-full h-3 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-600 to-cyan-400 transition-all duration-300 relative"
              style={{ width: `${research.researchProgress * 100}%` }}
            >
              <div className="absolute right-0 top-0 h-full w-2 bg-white animate-pulse"></div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="flex gap-1 bg-slate-900/50 rounded-lg p-1">
          {(['all', 'tier1', 'tier2', 'tier3'] as const).map(tier => (
            <button
              key={tier}
              onClick={() => setFilterTier(tier)}
              className={`px-3 py-1.5 rounded text-xs font-bold uppercase transition-all ${
                filterTier === tier 
                  ? 'bg-cyan-600 text-white' 
                  : 'text-slate-400 hover:text-white hover:bg-white/10'
              }`}
            >
              {tier === 'all' ? 'All' : tier.replace('tier', 'Tier ')}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowCompleted(!showCompleted)}
          className={`px-3 py-1.5 rounded text-xs font-bold uppercase transition-all ${
            showCompleted 
              ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-600/30' 
              : 'bg-slate-900/50 text-slate-400 border border-transparent'
          }`}
        >
          {showCompleted ? '✓ Show Completed' : 'Hide Completed'}
        </button>
      </div>

      {/* Research Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredTech.map(tech => {
          const isCompleted = research.completedTechs.includes(tech.id);
          const isActive = research.activeResearchId === tech.id;
          const isLocked = !tech.prerequisites.every(pre => research.completedTechs.includes(pre));
          const canAfford = stats.budget >= tech.cost;

          return (
            <div 
              key={tech.id} 
              className={`p-5 rounded-lg border relative overflow-hidden transition-all ${
                isCompleted 
                  ? 'bg-emerald-950/20 border-emerald-700/50' 
                  : isActive 
                    ? 'bg-cyan-900/20 border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.3)]' 
                    : isLocked 
                      ? 'bg-black/50 border-white/5 opacity-60' 
                      : 'bg-white/5 border-white/10 hover:border-white/30'
              }`}
            >
              {/* Active Progress Bar */}
              {isActive && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-800">
                  <div 
                    className="h-full bg-gradient-to-r from-cyan-600 to-cyan-400 transition-all duration-300"
                    style={{ width: `${research.researchProgress * 100}%` }}
                  />
                </div>
              )}

              {/* Header */}
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className={`font-bold text-lg ${isCompleted ? 'text-emerald-400' : 'text-white'}`}>
                    {tech.name}
                  </h3>
                  <p className="text-xs text-slate-400 mt-1">{tech.description}</p>
                </div>
                <div>
                  {isCompleted && (
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded border border-emerald-500/30">
                      ✓ DONE
                    </span>
                  )}
                  {isLocked && !isCompleted && <span className="text-2xl">🔒</span>}
                  {isActive && (
                    <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs font-bold rounded border border-cyan-500/30 animate-pulse">
                      IN PROGRESS
                    </span>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="flex gap-4 text-sm font-mono mb-4">
                <div className="flex items-center gap-1">
                  <span>⏱</span>
                  <span className="text-slate-400">{tech.duration * 2}s</span>
                </div>
                <div className="flex items-center gap-1">
                  <span>💰</span>
                  <span className={canAfford || isCompleted ? 'text-slate-300' : 'text-red-400'}>
                    ${tech.cost}
                  </span>
                </div>
                {tech.unlocks.length > 0 && (
                  <div className="flex items-center gap-1">
                    <span>🔓</span>
                    <span className="text-purple-400">{tech.unlocks.length} buildings</span>
                  </div>
                )}
              </div>

              {/* Prerequisites */}
              {tech.prerequisites.length > 0 && !isCompleted && (
                <div className="mb-4">
                  <div className="text-xs text-slate-500 uppercase mb-1">Requires:</div>
                  <div className="flex flex-wrap gap-1">
                    {tech.prerequisites.map(preId => {
                      const preTech = TECH_TREE.find(t => t.id === preId);
                      const isPreComplete = research.completedTechs.includes(preId);
                      return (
                        <span 
                          key={preId}
                          className={`text-xs px-2 py-0.5 rounded ${
                            isPreComplete 
                              ? 'bg-emerald-900/30 text-emerald-400' 
                              : 'bg-red-900/30 text-red-400'
                          }`}
                        >
                          {isPreComplete ? '✓' : '✗'} {preTech?.name || preId}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action Button */}
              {!isCompleted && !isLocked && (
                <button
                  onClick={() => { 
                    if (!isActive && canAfford) {
                      playUiSfx('money'); 
                      onStartResearch(tech.id); 
                    }
                  }}
                  disabled={isActive || !canAfford}
                  className={`w-full py-2.5 rounded-lg text-sm font-bold uppercase tracking-widest transition-all ${
                    isActive 
                      ? 'bg-cyan-600/50 text-cyan-200 cursor-default' 
                      : canAfford 
                        ? 'bg-cyan-600 hover:bg-cyan-500 text-white shadow-lg hover:shadow-cyan-500/25' 
                        : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  {isActive 
                    ? `Researching... ${Math.floor(research.researchProgress * 100)}%` 
                    : canAfford 
                      ? `Start Research ($${tech.cost})` 
                      : `Need $${tech.cost - stats.budget} more`
                  }
                </button>
              )}

              {isLocked && !isCompleted && (
                <div className="text-xs text-red-400 font-bold uppercase">
                  🔒 Complete prerequisites first
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredTech.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <div className="text-4xl mb-4">🔬</div>
          <p>No research projects match your filters</p>
        </div>
      )}
    </div>
  );
};

export default ResearchTab;
